import { useEffect, useRef, useState } from 'react';
import type { ApiClient } from '../api/client';
import { EventStream, type EventStreamState } from '../api/events';
import type { EventFilter, PlatformEvent } from '../api/types';

export interface PlatformEventsOptions {
  api: ApiClient;
  workspaceId: string;
  filter: EventFilter;
  /**
   * snapshot応答のeventCursor。nullなら接続時点以後だけを受け取る。
   * snapshotとの取りこぼしを避けたい画面はcursorが取れるまで enabled=false にする。
   */
  cursor: string | null;
  /** 購読するかどうか。falseの間は接続しない。 */
  enabled: boolean;
  onEvent(event: PlatformEvent): void;
}

/**
 * snapshotのcursorから購読を始める。cursorやfilterが変わったら購読を張り直す。
 * 期限切れ(410)は再接続では回復しないので expired を返し、呼び出し側にsnapshotの取り直しを促す。
 */
export function usePlatformEvents(options: PlatformEventsOptions): EventStreamState {
  const [state, setState] = useState<EventStreamState>('closed');
  const onEvent = useRef(options.onEvent);
  onEvent.current = options.onEvent;

  const { api, workspaceId, cursor, enabled } = options;
  const { appId, runId, requestId } = options.filter;

  useEffect(() => {
    if (!enabled) return;
    const stream = new EventStream(
      api,
      {
        workspaceId,
        filter: {
          ...(appId ? { appId } : {}),
          ...(runId ? { runId } : {}),
          ...(requestId ? { requestId } : {}),
        },
        ...(cursor ? { cursor } : {}),
      },
      {
        onEvent: (event) => onEvent.current(event),
        onState: setState,
      },
    );
    void stream.start();
    return () => stream.close();
  }, [api, workspaceId, appId, runId, requestId, cursor, enabled]);

  return state;
}
